import type { Sessao } from './auth';
import { chamar } from './encomendas-fluxo';

/**
 * ENCOMENDAS → REGISTRAR FALTA — cliente HTTP. O corpo do clique só diz QUAL produto, a consulta
 * (o atendimento) e o termo buscado: estoque, pessoa, hora e classificação são do servidor.
 */

export type ClassificacaoDaFalta = 'RUPTURA' | 'DIVERGENCIA' | 'SEM_LEITURA';
export type FonteDoEstoque = 'HOS_AO_VIVO' | 'ESPELHO' | 'SEM_LEITURA';
export type NivelDoAlerta = 'ATENCAO' | 'PRIORIDADE' | 'CRITICO';
export type VisibilidadeDoRanking = 'PUBLICO' | 'PROPRIA_POSICAO' | 'SO_ADMIN';

export interface Medalha {
  readonly codigo: string;
  readonly nome: string;
  readonly emoji: string;
  readonly minimo: number;
}

export interface Progresso {
  readonly atual: Medalha | null;
  readonly proxima: Medalha | null;
  readonly faltam: number | null;
}

export interface FaltaRegistrada {
  readonly id: string;
  readonly registradaEm: string;
  readonly repetida: boolean;
  readonly classificacao: ClassificacaoDaFalta;
  readonly estoqueHos: number | null;
  readonly estoqueFonte: FonteDoEstoque;
  readonly elegivelCampanha: boolean;
  readonly campanha: {
    readonly id: string;
    readonly nome: string;
    readonly validas: number;
    readonly progresso: Progresso;
    readonly medalhaNova: Medalha | null;
  } | null;
}

export interface ProdutoEmFalta {
  readonly productId: string;
  readonly descricao: string;
  readonly codigoHos: string | null;
  readonly ean: string | null;
  readonly fabricante: string | null;
  readonly foto: string | null;
  readonly estoqueAtual: number | null;
  readonly estoqueNoUltimoRegistro: number | null;
  readonly faltas: number;
  readonly divergencias: number;
  readonly faltas24h: number;
  readonly faltasHoje: number;
  readonly ultimaEm: string;
  readonly status: ClassificacaoDaFalta;
  readonly alerta: NivelDoAlerta | null;
}

export interface PainelDasFaltas {
  readonly indicadores: {
    readonly hoje: number;
    readonly ultimos7: number;
    readonly noPeriodo: number;
    readonly produtosDiferentes: number;
    readonly produtosComDivergencia: number;
    readonly viraramEncomenda: number;
    readonly validasCampanha: number;
  };
  readonly produtos: readonly ProdutoEmFalta[];
  readonly divergencias: readonly ProdutoEmFalta[];
  readonly alertas: readonly ProdutoEmFalta[];
  readonly demandaNaoAtendida: readonly {
    readonly productId: string;
    readonly descricao: string;
    readonly vezes: number;
  }[];
  readonly colaboradores: readonly {
    readonly accountId: string;
    readonly nome: string;
    readonly registros: number;
    readonly produtos: number;
    readonly divergencias: number;
    readonly validasCampanha: number;
  }[];
}

export interface RegrasDasFaltas {
  readonly repeticaoSegundos: number;
  readonly intervaloValidoMin: number;
  readonly maxValidasPorProdutoDia: number;
  readonly maxValidasPorDia: number;
  readonly alertaAtencao: number;
  readonly alertaPrioridade: number;
  readonly alertaCritico: number;
}

export interface OcorrenciaDeFalta {
  readonly id: string;
  readonly em: string;
  readonly nome: string;
  readonly estoqueHos: number | null;
  readonly estoqueFonte: FonteDoEstoque;
  readonly classificacao: ClassificacaoDaFalta;
  readonly termoBusca: string | null;
  readonly elegivelCampanha: boolean;
  readonly motivoInelegivel: string | null;
  readonly invalidada: {
    readonly em: string;
    readonly por: string;
    readonly motivo: string;
  } | null;
}

export interface DetalheDaFalta {
  readonly produto: {
    readonly id: string;
    readonly descricao: string;
    readonly ean: string | null;
    readonly codigoHos: string | null;
    readonly fabricante: string | null;
    readonly principioAtivo: string | null;
    readonly foto: string | null;
    readonly estoqueAtual: number | null;
    readonly estoqueVistoEm: string | null;
  };
  readonly total: number;
  readonly divergencias: number;
  readonly primeiraEm: string | null;
  readonly ultimaEm: string | null;
  readonly ultimos7: number;
  readonly ultimos30: number;
  readonly porDia: number | null;
  readonly ocorrencias: readonly OcorrenciaDeFalta[];
}

export interface CampanhaDeFaltas {
  readonly id: string;
  readonly nome: string;
  readonly descricao: string | null;
  readonly inicio: string;
  readonly fim: string;
  readonly ativa: boolean;
  readonly emVigor: boolean;
  readonly medalhas: readonly Medalha[];
  readonly rankingVisibilidade: VisibilidadeDoRanking;
  readonly atualizadoEm: string;
}

export interface MinhasFaltas {
  readonly registrosTotal: number;
  readonly registros30: number;
  readonly campanha: {
    readonly id: string;
    readonly nome: string;
    readonly descricao: string | null;
    readonly inicio: string;
    readonly fim: string;
    readonly validas: number;
    readonly progresso: Progresso;
    readonly medalhas: readonly Medalha[];
    readonly posicao: number | null;
    readonly participantes: number | null;
    readonly ranking:
      | readonly {
          readonly posicao: number;
          readonly nome: string;
          readonly validas: number;
          readonly euMesmo: boolean;
        }[]
      | null;
    readonly rankingVisibilidade: VisibilidadeDoRanking;
  } | null;
}

export interface FiltroDoPainel {
  readonly de: string;
  readonly ate: string;
  readonly q?: string;
  readonly fabricante?: string;
  readonly conta?: string;
  readonly situacao?: 'RUPTURA' | 'DIVERGENCIA';
  readonly origem?: 'ENCOMENDAS_CONSULTA' | 'MIA_WHATSAPP' | 'EQUIPE_WHATSAPP';
  readonly minimo?: number;
}

export const registrarFalta = (
  s: Sessao,
  corpo: { produtoId: string; consultaId: string | null; termoBusca: string | null },
) =>
  chamar<{ falta: FaltaRegistrada }>('/api/encomendas/faltas', {
    metodo: 'POST',
    corpo,
    sessao: s,
  });

export const lerMinhasFaltas = () =>
  chamar<{ faltas: MinhasFaltas }>('/api/encomendas/faltas/minhas');

export function lerPainelDasFaltas(f: FiltroDoPainel) {
  const q = new URLSearchParams({ de: f.de, ate: f.ate });
  if (f.q?.trim()) q.set('q', f.q.trim());
  if (f.fabricante?.trim()) q.set('fabricante', f.fabricante.trim());
  if (f.conta) q.set('conta', f.conta);
  if (f.situacao) q.set('situacao', f.situacao);
  if (f.origem) q.set('origem', f.origem);
  if (f.minimo && f.minimo > 1) q.set('minimo', String(f.minimo));
  return chamar<{ painel: PainelDasFaltas; regras: RegrasDasFaltas }>(
    `/api/encomendas/faltas/painel?${q.toString()}`,
  );
}

export const lerColaboradoresComFalta = () =>
  chamar<{ colaboradores: readonly { accountId: string; nome: string }[] }>(
    '/api/encomendas/faltas/colaboradores',
  );

export const lerDetalheDaFalta = (productId: string) =>
  chamar<{ detalhe: DetalheDaFalta }>(
    `/api/encomendas/faltas/produtos/${encodeURIComponent(productId)}`,
  );

export const invalidarFalta = (s: Sessao, id: string, motivo: string) =>
  chamar<undefined>(`/api/encomendas/faltas/${encodeURIComponent(id)}/invalidar`, {
    metodo: 'POST',
    corpo: { motivo },
    sessao: s,
  });

export const lerCampanhas = () =>
  chamar<{
    campanhas: readonly CampanhaDeFaltas[];
    ranking: readonly { accountId: string; nome: string; validas: number; posicao: number }[];
  }>('/api/encomendas/faltas/campanhas');

export const salvarCampanha = (
  s: Sessao,
  c: {
    id: string | null;
    nome: string;
    descricao: string | null;
    inicio: string;
    fim: string;
    ativa: boolean;
    medalhas: readonly Medalha[];
    rankingVisibilidade: VisibilidadeDoRanking;
  },
) =>
  chamar<{ campanha: CampanhaDeFaltas }>('/api/encomendas/faltas/campanhas', {
    metodo: 'POST',
    corpo: c,
    sessao: s,
  });

/* ---------------- TXT do SmartPed ---------------- */

export type MotivoForaDoCotac = 'COM_ESTOQUE' | 'ESTOQUE_NAO_LIDO' | 'SEM_EAN' | 'SEM_CODIGO';

export interface CandidatoAoCotac {
  readonly productId: string;
  readonly descricao: string;
  readonly codigoHos: string | null;
  readonly ean: string | null;
  readonly fabricante: string | null;
  readonly faltas: number;
  readonly estoqueAtual: number | null;
}

export interface CandidatosAoCotac {
  readonly entram: readonly CandidatoAoCotac[];
  readonly deFora: readonly (CandidatoAoCotac & { readonly motivo: MotivoForaDoCotac })[];
}

export const ROTULO_DE_FORA_DO_COTAC: Readonly<Record<MotivoForaDoCotac, string>> = {
  COM_ESTOQUE: 'Tem estoque no HOS',
  ESTOQUE_NAO_LIDO: 'Estoque não lido',
  SEM_EAN: 'Sem EAN válido',
  SEM_CODIGO: 'Sem código HOS válido',
};

export function lerCandidatosAoCotac(f: FiltroDoPainel) {
  const q = new URLSearchParams({ de: f.de, ate: f.ate });
  if (f.q?.trim()) q.set('q', f.q.trim());
  if (f.fabricante?.trim()) q.set('fabricante', f.fabricante.trim());
  if (f.conta) q.set('conta', f.conta);
  if (f.situacao) q.set('situacao', f.situacao);
  if (f.origem) q.set('origem', f.origem);
  if (f.minimo && f.minimo > 1) q.set('minimo', String(f.minimo));
  return chamar<{ candidatos: CandidatosAoCotac }>(
    `/api/encomendas/faltas/smartped?${q.toString()}`,
  );
}

export const gerarCotac = (
  s: Sessao,
  itens: readonly { readonly produtoId: string; readonly quantidade: number }[],
) =>
  chamar<{
    arquivo: {
      readonly nome: string;
      readonly conteudo: string;
      readonly linhas: number;
      readonly unidades: number;
    };
  }>('/api/encomendas/faltas/smartped/arquivo', { metodo: 'POST', corpo: { itens }, sessao: s });

/* ---------------- textos ---------------- */

export const ROTULO_DA_CLASSIFICACAO: Readonly<Record<ClassificacaoDaFalta, string>> = {
  RUPTURA: 'Sem estoque',
  DIVERGENCIA: 'Estoque divergente',
  SEM_LEITURA: 'Estoque não lido',
};

export const ROTULO_DO_ALERTA: Readonly<Record<NivelDoAlerta, string>> = {
  ATENCAO: 'Atenção',
  PRIORIDADE: 'Prioridade',
  CRITICO: 'Alta demanda não atendida',
};

export const ROTULO_DO_MOTIVO: Readonly<Record<string, string>> = {
  INTERVALO: 'mesma pessoa e produto em pouco tempo',
  LIMITE_PRODUTO_DIA: 'passou do limite do produto no dia',
  LIMITE_DIA: 'passou do limite do dia',
};

export const MEDALHAS_SUGERIDAS: readonly Medalha[] = [
  { codigo: 'bronze', nome: 'Olho no Estoque', emoji: '🥉', minimo: 10 },
  { codigo: 'prata', nome: 'Caçador de Faltas', emoji: '🥈', minimo: 30 },
  { codigo: 'ouro', nome: 'Guardião do Estoque', emoji: '🥇', minimo: 60 },
  { codigo: 'mestre', nome: 'Mestre da Ruptura', emoji: '🏆', minimo: 100 },
];

/** "AAAA-MM-DD" de hoje em Brasília (deslocado N dias). */
export function diaBr(deslocar = 0): string {
  const d = new Date(Date.now() + deslocar * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(d);
}

/** Um id de consulta (atendimento) — crypto.randomUUID quando há, senão v4 manual. */
export function novaConsulta(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c) c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
